import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import DatePicker from '../../components/forms/formDatePicker';
import { toLocalDateString } from '../../utils/helper';
import FormInput from '../../components/forms/formInput';
import { getStatusMeta, getStatusOptions } from '../../constants/jobStatus';
import { JOB_ACTIVITY_FILTERS } from '../../utils/routeActivity';
import { MISC_LABELS } from '../../constants/labels';

const FilterIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
);
const ChevIcon = () => (
    <svg className="ic chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const ALL = 'All jobs';
const QUICK_PERIOD_KEYS = [ALL, 'Today', 'Tomorrow', 'This Week', 'This Month', 'Last Month'];
const QUICK_PERIOD_I18N = {
    [ALL]: 'jobs.allJobs',
    'Today': 'jobs.today',
    'Tomorrow': 'jobs.tomorrow',
    'This Week': 'jobs.thisWeek',
    'This Month': 'jobs.thisMonth',
    'Last Month': 'jobs.lastMonth',
};

const dayBounds = (offsetDays = 0) => {
    const s = new Date(); s.setDate(s.getDate() + offsetDays); s.setHours(0, 0, 0, 0);
    const e = new Date(s); e.setHours(23, 59, 59, 999);
    return { start: s, end: e };
};

/* Monday 00:00 → Sunday 23:59:59 of the week containing today. */
const thisWeek = () => {
    const s = new Date();
    const day = s.getDay();                    // 0=Sun … 6=Sat
    s.setDate(s.getDate() + (day === 0 ? -6 : 1 - day));
    s.setHours(0, 0, 0, 0);
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    e.setHours(23, 59, 59, 999);
    return { start: s, end: e };
};

const monthBounds = (monthOffset = 0) => {
    const t = new Date();
    const s = new Date(t.getFullYear(), t.getMonth() + monthOffset, 1);
    const e = new Date(t.getFullYear(), t.getMonth() + monthOffset + 1, 0, 23, 59, 59, 999);
    return { start: s, end: e };
};

// Every quick period as { start, end }; ALL is "no bounds".
const PERIOD_RANGES = {
    [ALL]: () => ({ start: null, end: null }),
    'Today': () => dayBounds(0),
    'Tomorrow': () => dayBounds(1),
    'This Week': thisWeek,
    'This Month': () => monthBounds(0),
    'Last Month': () => monthBounds(-1),
};

const sameDay = (a, b) => !!a && !!b && new Date(a).toDateString() === new Date(b).toDateString();

// Which quick period (if any) a { start, end } corresponds to.
const periodFor = ({ start, end }) => {
    if (!start && !end) return ALL;
    return QUICK_PERIOD_KEYS.find((p) => {
        if (p === ALL) return false;
        const r = PERIOD_RANGES[p]();
        return sameDay(r.start, start) && sameDay(r.end, end);
    }) || '';
};

const describe = ({ start, end, status, activity, needsReschedule }, tr) => {
    const prefix = [
        status ? getStatusMeta(status).label : '',
        activity ? JOB_ACTIVITY_FILTERS.find(a => a.value === activity)?.label : '',
        needsReschedule ? MISC_LABELS.needsRescheduling : '',
    ].filter(Boolean).map(s => `${s} · `).join('');
    const period = periodFor({ start, end });
    if (period) return `${prefix}${tr ? tr(QUICK_PERIOD_I18N[period]) : period}`;
    const parts = [start && toLocalDateString(start, 'dd.MM.yyyy'), end && toLocalDateString(end, 'dd.MM.yyyy')].filter(Boolean);
    return `${prefix}${parts.join(' – ') || (tr ? tr(QUICK_PERIOD_I18N[ALL]) : ALL)}`;
};

/**
 * Date / status filter for the Jobs page. `value` is the page's current
 * filter ({ start, end, status }) — the panel mirrors it, so a range applied
 * by the page itself (e.g. arriving from an Overview card) is what the panel
 * shows and what Reset/Apply act on, instead of a stale local copy.
 */
const JobsFilter = ({ value, isFiltering, onFilter }) => {

    const { t } = useTranslation();
    const [isShow, setIsShow] = useState(false)
    const [status, setStatus] = useState(value?.status ?? '')
    const [activity, setActivity] = useState(value?.activity ?? '')
    const [needsReschedule, setNeedsReschedule] = useState(!!value?.needsReschedule)
    const [dateFrom, setDateFrom] = useState(value?.start ?? null)
    const [dateTo, setDateTo] = useState(value?.end ?? null)
    const [filterName, setFilterName] = useState(() => describe(value ?? {}, t))
    const [activePeriod, setActivePeriod] = useState(() => periodFor(value ?? {}))

    useEffect(() => {
        const v = value ?? {};
        setStatus(v.status ?? '');
        setActivity(v.activity ?? '');
        setNeedsReschedule(!!v.needsReschedule);
        setDateFrom(v.start ?? null);
        setDateTo(v.end ?? null);
        setFilterName(describe(v, t));
        setActivePeriod(periodFor(v));
    }, [value?.start, value?.end, value?.status, value?.activity, value?.needsReschedule]); // eslint-disable-line react-hooks/exhaustive-deps

    const activeCount = [status, activity, needsReschedule ? 'reschedule' : '', periodFor({ start: dateFrom, end: dateTo }) !== ALL ? 'range' : ''].filter(Boolean).length

    const close = () => setTimeout(() => setIsShow(false), 700);

    const apply = (start, end, nextStatus, nextActivity = activity, nextNeedsReschedule = needsReschedule) => {
        onFilter({ start: start ?? null, end: end ?? null, status: nextStatus ?? '', activity: nextActivity ?? '', needsReschedule: !!nextNeedsReschedule })
        close()
    }

    const handleFilter = (e) => {
        e.preventDefault()
        e.stopPropagation()
        apply(dateFrom, dateTo, status, activity, needsReschedule)
    }

    const handleReset = () => apply(null, null, '', '', false)

    const handleQuick = (period) => () => {
        const { start, end } = PERIOD_RANGES[period]()
        apply(start, end, status, activity, needsReschedule)
    }

    return (
        <>
            <button type="button" className={`filter-toggle${isShow ? ' on' : ''}`} onClick={() => setIsShow(s => !s)}>
                <FilterIcon /> {t('jobs.filters')}
                {activeCount > 0 && <span className="cnt">{activeCount}</span>}
                <ChevIcon />
            </button>

            {isShow && <div className="filter-backdrop" onClick={() => setIsShow(false)} aria-hidden="true" />}

            {isShow && (
                <Form onSubmit={handleFilter} className="filter-panel as-sheet">
                    <div className="filter-sheet-head">
                        <span className="t">{t('jobs.filters')}</span>
                        <button type="button" className="x" onClick={() => setIsShow(false)} aria-label={t('jobs.closeFilters')}>
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                    </div>
                    <div className="filter-quick">
                        {QUICK_PERIOD_KEYS.map((period) => (
                            <button
                                key={period}
                                type="button"
                                className={`qchip${activePeriod === period ? ' on' : ''}`}
                                onClick={handleQuick(period)}
                            >
                                {t(QUICK_PERIOD_I18N[period])}
                            </button>
                        ))}
                    </div>

                    <div className="filter-grid">
                        <Form.Group>
                            <Form.Label>{t('jobs.status')}</Form.Label>
                            <FormInput value={status} as="select" onChange={(e) => setStatus(e.target.value)}>
                                <option value="">{t('jobs.allStatus')}</option>
                                {getStatusOptions().map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </FormInput>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('jobs.activity')}</Form.Label>
                            <FormInput value={activity} as="select" onChange={(e) => setActivity(e.target.value)}>
                                {JOB_ACTIVITY_FILTERS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </FormInput>
                        </Form.Group>
                        <DatePicker className='form-control' label={t('jobs.dateFrom')} placeholderText={t('jobs.anyDate')}
                            selected={dateFrom} onChange={setDateFrom} maxDate={dateTo || undefined} portalId="sbr-dp-portal" isClearable
                        />
                        <DatePicker className='form-control' label={t('jobs.dateTo')} placeholderText={t('jobs.anyDate')}
                            selected={dateTo} onChange={setDateTo} minDate={dateFrom || undefined} portalId="sbr-dp-portal" isClearable
                        />
                        <Form.Group>
                            <Form.Check type="checkbox" id="jobs-filter-needs-reschedule" checked={needsReschedule}
                                onChange={(e) => setNeedsReschedule(e.target.checked)}
                                label={t('jobs.needsReschedulingOnly')} />
                        </Form.Group>
                    </div>

                    <div className="filter-foot">
                        <span className="meta">{filterName}</span>
                        <div className="sp">
                            <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={handleReset}>{t('jobs.reset')}</button>
                            <button type="submit" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isFiltering}>
                                {isFiltering ? t('jobs.filtering') : t('jobs.applyFilters')}
                            </button>
                        </div>
                    </div>
                </Form>
            )}
        </>
    )
}

export default JobsFilter